/**
 * /dashboard/website — Website management page for the logged-in agent.
 * Shows current template + published URL, plus the self-serve "Manage
 * Website Subscription" section (cancel / reactivate / restore).
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  ExternalLink, Pencil, Globe, Loader2,
  CreditCard, AlertTriangle, CheckCircle2, RotateCcw, Pause, Play, Eye, Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  active:           { label: 'Active',            tone: 'text-emerald-600' },
  cancel_scheduled: { label: 'Cancel scheduled',  tone: 'text-amber-600'   },
  paused_by_owner:  { label: 'Paused (Vacation)', tone: 'text-sky-600'     },
  expired:          { label: 'Expired',           tone: 'text-red-600'     },
  past_due:         { label: 'Past due',          tone: 'text-red-600'     },
  canceled:         { label: 'Canceled',          tone: 'text-red-600'     },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

export default function DashboardWebsitePage() {
  const { token, user } = useAuth();
  const agent = user || {};
  const [site, setSite] = useState(null);
  const [pausedInfo, setPausedInfo] = useState(null); // { id, slug } when site is paused
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // 'cancel' | 'reactivate' | 'pause' | 'resume' | 'preview'

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadAll = useCallback(async () => {
    try {
      if (!agent?.slug) return;
      const r = await axios.get(`${API}/customer-websites/by-slug/${agent.slug}/full`);
      if (r.data?.paused) {
        setSite(null);
        setPausedInfo({ id: r.data.id, slug: r.data.slug });
      } else {
        setSite(r.data);
        setPausedInfo(null);
      }
    } catch { /* not yet published */ }
    try {
      const r = await axios.get(`${API}/website-subscriptions/me`, { headers: authHeaders });
      setSubscription(r.data);
    } catch { setSubscription(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.slug, token]);

  useEffect(() => {
    (async () => { await loadAll(); setLoading(false); })();
  }, [loadAll]);

  const callLifecycle = async (action) => {
    setActionLoading(action);
    try {
      const r = await axios.post(
        `${API}/website-subscriptions/${action}`,
        { agent_id: agent.id, tenant_id: agent.tenant_id || undefined },
        { headers: authHeaders },
      );
      if (r.data?.success === false) {
        toast.error(r.data?.message || 'Something went wrong.');
      } else {
        const okMsg = {
          cancel:     'Cancellation scheduled — your site will stay live until the end of your billing period.',
          reactivate: 'Website Builder restored.',
          pause:      'Vacation Hold enabled — your website is now hidden. Billing continues as usual.',
          resume:     'Website is live again.',
        }[action] || 'Done.';
        toast.success(okMsg);
        await loadAll();
      }
    } catch (e) {
      const data = e.response?.data;
      toast.error(data?.message || data?.detail || 'Action failed. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const mintPreviewToken = async () => {
    if (!pausedInfo?.id) {
      toast.error('Could not find your website.');
      return null;
    }
    const r = await axios.post(
      `${API}/customer-websites/${pausedInfo.id}/preview-token`,
      {},
      { headers: authHeaders },
    );
    if (!r.data?.token) {
      toast.error('Could not generate preview link.');
      return null;
    }
    const path = `/sites/${pausedInfo.slug}?preview_token=${encodeURIComponent(r.data.token)}`;
    return { path, absolute: `${window.location.origin}${path}` };
  };

  const openPrivatePreview = async () => {
    setActionLoading('preview');
    try {
      const link = await mintPreviewToken();
      if (!link) return;
      window.open(link.path, '_blank', 'noopener,noreferrer');
      toast.success('Private preview opened — link valid for 15 minutes.');
    } catch (e) {
      const data = e.response?.data;
      toast.error(data?.message || data?.detail || 'Could not generate preview link.');
    } finally {
      setActionLoading(null);
    }
  };

  const copyPreviewLink = async () => {
    setActionLoading('copy-preview');
    try {
      const link = await mintPreviewToken();
      if (!link) return;
      try {
        await navigator.clipboard.writeText(link.absolute);
      } catch {
        // Fallback for browsers without clipboard API permissions
        const ta = document.createElement('textarea');
        ta.value = link.absolute;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
      }
      toast.success('Private preview link copied. It expires in 15 minutes.');
    } catch (e) {
      const data = e.response?.data;
      toast.error(data?.message || data?.detail || 'Could not copy preview link.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex justify-center" data-testid="dashboard-website-loading">
        <Loader2 className="animate-spin text-muted-foreground" size={24} />
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-10 max-w-5xl space-y-6" data-testid="dashboard-website-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Your website</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage the public site Agent9 hosts for {agent?.brokerage_name || 'your business'}.
          </p>
        </div>
        {site && (
          <a href={`/sites/${site.slug}`} target="_blank" rel="noreferrer"
             data-testid="dashboard-website-view-btn"
             className="text-sm text-emerald-600 hover:text-emerald-500 inline-flex items-center gap-1">
            View live site <ExternalLink size={14} />
          </a>
        )}
      </header>

      {site ? (
        <Card data-testid="dashboard-website-current-site">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Globe size={16}/> Current website</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Row label="Template"   value={site.template_snapshot?.name || site.template_id} />
            <Row label="Status"     value={site.status || 'published'} />
            <Row label="Slug"       value={site.slug} mono />
            <Row label="Public URL" value={`/sites/${site.slug}`} link />
            {site.custom_domain && <Row label="Custom domain" value={site.custom_domain} mono />}
            <div className="pt-3">
              <Button asChild variant="secondary" data-testid="dashboard-website-switch-btn">
                <Link to="/dashboard/website/templates">
                  <Pencil size={14} className="mr-1" /> Switch template
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : pausedInfo ? (
        <Card data-testid="dashboard-website-paused-site">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Globe size={16}/> Your website is hidden</CardTitle>
            <CardDescription>
              The public site at <code className="px-1 py-0.5 bg-muted rounded">/sites/{pausedInfo.slug}</code> is
              currently paused. Use a private preview to check it before you bring it back online.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                data-testid="preview-private-site-btn"
                onClick={openPrivatePreview}
                disabled={actionLoading === 'preview'}
              >
                {actionLoading === 'preview'
                  ? <><Loader2 size={14} className="mr-1 animate-spin"/> Opening preview…</>
                  : <><Eye size={14} className="mr-1"/> Preview private site</>}
              </Button>
              <Button
                variant="outline"
                data-testid="copy-preview-link-btn"
                onClick={copyPreviewLink}
                disabled={actionLoading === 'copy-preview'}
              >
                {actionLoading === 'copy-preview'
                  ? <><Loader2 size={14} className="mr-1 animate-spin"/> Copying…</>
                  : <><Copy size={14} className="mr-1"/> Copy preview link</>}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Private preview links expire after 15 minutes and only unlock this one site.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Globe size={16}/> No website yet</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Pick a template to publish your website. It takes about 30 seconds.
            </p>
            <Button asChild data-testid="dashboard-website-pick-cta">
              <Link to={`/onboarding/website-template?agent_id=${agent?.id || ''}&tenant_id=${agent?.tenant_id || ''}&slug=${agent?.slug || ''}`}>
                Choose a template
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <ManageSubscriptionCard
        subscription={subscription}
        actionLoading={actionLoading}
        onCancel={() => callLifecycle('cancel')}
        onReactivate={() => callLifecycle('reactivate')}
        onPause={() => callLifecycle('pause')}
        onResume={() => callLifecycle('resume')}
      />
    </div>
  );
}

function ManageSubscriptionCard({ subscription, actionLoading, onCancel, onReactivate, onPause, onResume }) {
  if (!subscription || !subscription.exists) {
    return (
      <Card data-testid="dashboard-website-subscription-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard size={16} /> Manage Website Subscription
          </CardTitle>
          <CardDescription>
            You don&apos;t have an active Website Builder subscription yet.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const status = subscription.status;
  const statusInfo = STATUS_LABELS[status] || { label: status, tone: 'text-foreground' };
  const isActive          = status === 'active';
  const isCancelScheduled = status === 'cancel_scheduled';
  const isPaused          = status === 'paused_by_owner';
  const isInactive        = ['expired', 'past_due', 'canceled'].includes(status);
  const pausesRemaining   = subscription.pauses_remaining;
  const canPause          = (isActive || isCancelScheduled) && (pausesRemaining ?? 1) > 0;

  return (
    <Card data-testid="dashboard-website-subscription-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard size={16} /> Manage Website Subscription
        </CardTitle>
        <CardDescription>
          $29 / month — billed through Agent9. Cancel any time. Your data is never deleted.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Row
          label="Plan status"
          value={
            <span
              data-testid="subscription-status-value"
              className={`font-medium ${statusInfo.tone}`}
            >
              {statusInfo.label}
            </span>
          }
        />

        {isActive && (
          <Row label="Renews on" value={formatDate(subscription.renews_at)} />
        )}

        {isCancelScheduled && (
          <>
            <Row label="Site live until" value={formatDate(subscription.access_until)} />
            <div
              data-testid="subscription-cancel-banner"
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
            >
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>
                Your subscription is scheduled to cancel. Your website stays live until{' '}
                <strong>{formatDate(subscription.access_until)}</strong>. Reactivate before
                then and you won&apos;t be charged again — your site stays online without interruption.
              </span>
            </div>
          </>
        )}

        {isPaused && (
          <>
            <Row label="Paused since"   value={formatDate(subscription.paused_at)} />
            <Row label="Auto-resumes on" value={formatDate(subscription.auto_resume_at)} />
            <Row label="Next charge"    value={formatDate(subscription.renews_at)} />
            <div
              data-testid="subscription-paused-by-owner-banner"
              className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
            >
              <Pause size={16} className="mt-0.5 shrink-0" />
              <span>
                Your website is hidden from the public until you resume it (or
                automatically on <strong>{formatDate(subscription.auto_resume_at)}</strong>).
                <em className="not-italic"> Billing continues as usual</em> — this is a
                vacation hold, not a billing pause.
              </span>
            </div>
          </>
        )}

        {isInactive && (
          <div
            data-testid="subscription-paused-banner"
            className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
          >
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>
              Your website is currently paused. Restore your subscription to bring it back
              online — your template, content, leads, domain, and analytics are all preserved.
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          {isActive && (
            <CancelButton
              onConfirm={onCancel}
              loading={actionLoading === 'cancel'}
              renewsAt={subscription.renews_at}
            />
          )}

          {isCancelScheduled && (
            <Button
              data-testid="reactivate-subscription-btn"
              onClick={onReactivate}
              disabled={actionLoading === 'reactivate'}
            >
              {actionLoading === 'reactivate'
                ? <><Loader2 size={14} className="mr-1 animate-spin"/> Reactivating…</>
                : <><CheckCircle2 size={14} className="mr-1"/> Reactivate Website Builder</>}
            </Button>
          )}

          {(isActive || isCancelScheduled) && (
            <PauseButton
              onConfirm={onPause}
              loading={actionLoading === 'pause'}
              disabled={!canPause}
              pausesRemaining={pausesRemaining}
            />
          )}

          {isPaused && (
            <Button
              data-testid="resume-subscription-btn"
              onClick={onResume}
              disabled={actionLoading === 'resume'}
            >
              {actionLoading === 'resume'
                ? <><Loader2 size={14} className="mr-1 animate-spin"/> Resuming…</>
                : <><Play size={14} className="mr-1"/> Resume Website</>}
            </Button>
          )}

          {isInactive && (
            <Button
              data-testid="restore-subscription-btn"
              onClick={onReactivate}
              disabled={actionLoading === 'reactivate'}
            >
              {actionLoading === 'reactivate'
                ? <><Loader2 size={14} className="mr-1 animate-spin"/> Restoring…</>
                : <><RotateCcw size={14} className="mr-1"/> Restore Website Builder</>}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function PauseButton({ onConfirm, loading, disabled, pausesRemaining }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="pause-subscription-btn"
          disabled={loading || disabled}
        >
          {loading
            ? <><Loader2 size={14} className="mr-1 animate-spin"/> Pausing…</>
            : <><Pause size={14} className="mr-1"/> Pause Website (Vacation Mode)</>}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="pause-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Enable Vacation Hold?</AlertDialogTitle>
          <AlertDialogDescription>
            Your website will be hidden from the public, but billing will continue. You can
            resume anytime within the next <strong>60 days</strong>, after which your site
            will automatically come back online.
            {typeof pausesRemaining === 'number' && (
              <> You have <strong>{pausesRemaining}</strong> Vacation Hold{pausesRemaining === 1 ? '' : 's'} remaining this year.</>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="pause-confirm-keep">Never mind</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            data-testid="pause-confirm-proceed"
          >
            Yes, hide my site
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function CancelButton({ onConfirm, loading, renewsAt }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="cancel-subscription-btn"
          disabled={loading}
        >
          {loading
            ? <><Loader2 size={14} className="mr-1 animate-spin"/> Canceling…</>
            : 'Cancel Website Builder'}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="cancel-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel Website Builder?</AlertDialogTitle>
          <AlertDialogDescription>
            Your website will stay live until the end of your current billing period
            ({formatDate(renewsAt)}). You will not be charged again. You can reactivate
            any time before then to continue without interruption.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cancel-confirm-keep">Keep subscription</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            data-testid="cancel-confirm-proceed"
          >
            Yes, cancel
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Row({ label, value, mono, link }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm border-b border-border/50 pb-2">
      <span className="text-muted-foreground">{label}</span>
      {link
        ? <a href={value} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">{value}</a>
        : (typeof value === 'string' || typeof value === 'number'
            ? <span className={mono ? 'font-mono text-xs text-right' : 'text-right'}>{value}</span>
            : <span className="text-right">{value}</span>)}
    </div>
  );
}
